import { GLYPH_CHOICES } from './glyphs';

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Affiché dans la case « aucun », pour montrer ce qui sera pris par défaut. */
  fallbackGlyph: string;
}

/**
 * Choix d'un pictogramme, dans une banque courte.
 *
 * Des emoji plutôt que des images téléversées : ils se rendent à n'importe
 * quelle taille, ne demandent ni stockage ni sauvegarde, et fonctionnent hors
 * ligne. La case « aucun » montre le glyphe qui prendra le relais, pour que
 * l'absence de choix ne soit pas un trou.
 */
export function GlyphPicker({ label, value, onChange, fallbackGlyph }: Props) {
  // Le repli occupe déjà la case « aucun » : le laisser aussi dans la banque
  // afficherait deux fois le même glyphe, pour un résultat identique.
  const choices = GLYPH_CHOICES.filter((glyph) => glyph !== fallbackGlyph);

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-sm text-ink-secondary">{label}</legend>
      <div className="grid grid-cols-8 gap-1.5">
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Aucun pictogramme"
          aria-pressed={value === ''}
          className={`flex aspect-square items-center justify-center rounded-lg border text-lg opacity-50 ${
            value === '' ? 'border-series bg-plane' : 'border-line'
          }`}
        >
          {fallbackGlyph}
        </button>

        {choices.map((glyph) => (
          <button
            key={glyph}
            type="button"
            onClick={() => onChange(glyph)}
            aria-label={`Pictogramme ${glyph}`}
            aria-pressed={value === glyph}
            className={`flex aspect-square items-center justify-center rounded-lg border text-lg ${
              value === glyph ? 'border-series bg-plane' : 'border-line'
            }`}
          >
            {glyph}
          </button>
        ))}
      </div>
    </fieldset>
  );
}
